import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";

import {
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  Grid, IconButton, Tooltip
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RemoveIcon from "@material-ui/icons/Remove";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";

import useTranslation from "src/translations/useTranslation";
import { CoordinateAnalysisMessages, TravelAnalysisMessages } from "@shared/languages/interfaces";

import { TravelAnalytics } from "@shared/interfaces/travel-analytics.interface";
import { Travel } from "@shared/interfaces/travel.interface";
import { Vehicle } from "@shared/interfaces/vehicle.interface";

import { createGlobalStyle } from "styled-components";

import {
  TravelMap, buildTravelRoute, generateAreaLabelOptions, generateCirclesOptions, generateDefaultMapFilter,
  generateMarkerOptions, generatePolygonsOptions, generatePolylinesOptions
} from "@components/Map/TravelMap";
import { TravelMapStylesheet } from "@components/Map/TravelMap/styles";
import TimeLineVehicleStates from "@components/TimeLines/TimeLineVehicleStates";

import utils from "@utils/useful-functions";
import useTravelAnalysisCoordinateView from "../../hooks/useTravelAnalysisCoordinateView";
import useTravelCoordinateViewMap from "../../hooks/useTravelCoordinateViewMap";

/** Forces MUI Tooltip to render above the fullscreen overlay (z-index 2000). */
const GlobalTooltipZIndex = createGlobalStyle`
  .MuiTooltip-popper {
    z-index: 2100 !important;
  }
`;

/** Height of the map in the center column: the replay, the timeline and the legend come below it */
const MAP_HEIGHT = "55vh";

/** Props for the TravelCoordinateViewModal. */
interface TravelCoordinateViewModalProps {
  open: boolean;
  onClose: () => void;
  travelId: string;
  vehicleDescription: string;
  numDoc?: string | null;
}

/**
 * Fullscreen overlay that displays the coordinate analysis view for a single travel.
 * Layout: States timeline (left) | Map (center) | Numbered commands list (right).
 * The map is the same as the travel detail's (route by leg, numbered states and alerts, replay, timeline and
 * legend); the pin of a command moves the replay cursor to that command.
 * Opened from AlertsDrilldownModal via a button per row.
 */
const TravelCoordinateViewModal: React.FC<TravelCoordinateViewModalProps> = ({
  open,
  onClose,
  travelId,
  vehicleDescription,
  numDoc
}) => {
  const { t } = useTranslation();
  const { coords, loading, fetchCoordinateView, reset } = useTravelAnalysisCoordinateView();
  const {
    travel, defaultRadiusMeters, zonePolygons, zonePolylines, loading: mapLoading, fetchMapData, resetMapData
  } = useTravelCoordinateViewMap();

  const [focusTime, setFocusTime] = useState<{ time: number, nonce: number } | undefined>();
  const [commandDetail, setCommandDetail] = useState<{ record: TravelAnalytics; tab: "general" | "content" | "result" } | null>(null);

  // Fetch data when opened
  useEffect(() => {
    if (open && travelId) {
      fetchCoordinateView(travelId);
      fetchMapData(travelId);
    }
    if (!open) {
      reset();
      resetMapData();
      setFocusTime(undefined);
    }
  }, [open, travelId]);

  // The travel with its vehicle; if it cannot be read, the map draws only the route
  const travelDetails = useMemo(() => travel || ({} as Travel), [travel]);
  const vehicle = useMemo(() => ({
    ...travel?.vehicle,
    type: travel?.vehicle?.type || {},
    states: travel?.vehicle?.states || [],
    alerts: travel?.vehicle?.alerts || []
  } as Vehicle), [travel]);

  const [mapFilter, setMapFilter] = useState(generateDefaultMapFilter(t, vehicle));

  useEffect(() => setMapFilter(generateDefaultMapFilter(t, vehicle)), [vehicle, t]);

  // Map data: the same builders as the travel detail map
  const markers = useMemo(() => [
    ...generateMarkerOptions(travelDetails, coords, vehicle, t),
    ...generateAreaLabelOptions(travelDetails, defaultRadiusMeters, t)
  ], [travelDetails, coords, vehicle, defaultRadiusMeters, t]);
  const route = useMemo(() => buildTravelRoute(coords), [coords]);
  const polylines = useMemo(() => [...generatePolylinesOptions(route.points), ...zonePolylines], [route, zonePolylines]);
  const circles = useMemo(() => generateCirclesOptions(travelDetails, defaultRadiusMeters), [travelDetails, defaultRadiusMeters]);
  const polygonPlaces = useMemo(() => generatePolygonsOptions(travelDetails), [travelDetails]);
  const destination = useMemo(() => (travelDetails?.destination?.latitude && travelDetails?.destination?.longitude
    ? { lat: Number(travelDetails.destination.latitude), lng: Number(travelDetails.destination.longitude) }
    : undefined), [travelDetails]);

  // Build vehicle mock for TimeLineVehicleStates
  const vehicleForTimeline = useMemo((): Vehicle => {
    const states = coords.filter((r) => !!r.vehicleState).map((r) => r.vehicleState);
    const alerts = coords.filter((r) => !!r.alert).map((r) => r.alert);

    return { states, alerts } as unknown as Vehicle;
  }, [coords]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      zIndex: 2000,
      backgroundColor: "#fff",
      display: "flex",
      flexDirection: "column"
    }}>
      <GlobalTooltipZIndex />
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "10px 16px",
        borderBottom: "1px solid #e0e0e0",
        flexShrink: 0
      }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: "16px" }}>
            {t(TravelAnalysisMessages.coordinateViewTitle)}
          </div>
          <div style={{ fontSize: "13px", color: "#757575", marginTop: "2px" }}>
            {t(TravelAnalysisMessages.coordinateViewVehicle)}: {vehicleDescription}
          </div>
          {numDoc && (
            <div style={{ fontSize: "13px", color: "#757575", marginTop: "1px" }}>
              {t(TravelAnalysisMessages.coordinateViewNF)}: {numDoc}
            </div>
          )}
        </div>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>

      {/* Content */}
      {loading || mapLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
          <CircularProgress size={48} />
        </div>
      ) : coords.length === 0 ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, color: "#999" }}>
          {t(CoordinateAnalysisMessages.noCommandsForThisPeriod)}
        </div>
      ) : (
        <Grid container style={{ flex: 1, overflow: "hidden" }}>
          {/* Left column — States + Alerts timeline */}
          <Grid item xs={3} style={{ height: "100%", overflow: "auto", borderRight: "1px solid #e0e0e0", padding: "8px", paddingTop: "16px" }}>
            <TimeLineVehicleStates vehicle={vehicleForTimeline} historicalTravel />
          </Grid>

          {/* Center column — the travel detail map */}
          <Grid item xs={6} style={{ height: "100%", overflow: "auto", padding: "0 8px" }}>
            <TravelMapStylesheet>
              <TravelMap
                markers={markers}
                polylines={polylines}
                polygons={zonePolygons}
                polygonPlaces={polygonPlaces}
                circles={circles}
                route={route}
                destination={destination}
                maxSpeed={travelDetails?.alert_maximum_speed || travelDetails?.vehicle?.maximum_speed_allowed || undefined}
                vehicleTypeId={travelDetails?.vehicle?.type?.id_vehicle_type}
                hasNote={!!(travelDetails?.num_doc || numDoc)}
                mapFilter={mapFilter}
                setMapFilter={setMapFilter}
                mapHeight={MAP_HEIGHT}
                focusTime={focusTime}
                t={t}
              />
            </TravelMapStylesheet>
          </Grid>

          {/* Right column — Numbered commands list */}
          <Grid item xs={3} style={{ height: "100%", overflow: "auto", borderLeft: "1px solid #e0e0e0", padding: "8px", paddingTop: "16px" }}>
            <div style={{ fontWeight: 600, marginBottom: "8px" }}>
              {t(CoordinateAnalysisMessages.commands)} ({coords.length})
            </div>
            {coords.map((record, index) => (
              <div
                key={record.binary_data || `cmd-${index}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 6px",
                  borderBottom: "1px solid #f0f0f0",
                  fontSize: "12px"
                }}
              >
                <Chip label={index + 1} size="small" style={{ minWidth: "32px", height: "20px" }} />
                <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {utils.formatDateIfHave(new Date(record.occurrence_date), "fullDate")}
                </span>
                <span style={{ display: "flex", alignItems: "center", flexShrink: 0, gap: "2px" }}>
                  {record.vehicleState && (
                    <Tooltip title={record.vehicleState.status.description} arrow>
                      <span style={{ display: "inline-flex" }}>
                        <ReactSVG
                          src={utils.getMarkerAccordingVehicleState(record.vehicleState.status.id_vehicle_state_type)}
                          beforeInjection={(svg) => {
                            svg.setAttribute("width", "16"); svg.setAttribute("height", "16");
                          }}
                        />
                      </span>
                    </Tooltip>
                  )}
                  {record.alert && (
                    <Tooltip title={record.alert.type.description} arrow>
                      <span style={{ display: "inline-flex" }}>
                        <ReactSVG
                          src={utils.getMarkerAccordingVehicleAlert(record.alert.type.id_alert_type)}
                          beforeInjection={(svg) => {
                            svg.setAttribute("width", "16"); svg.setAttribute("height", "16");
                          }}
                        />
                      </span>
                    </Tooltip>
                  )}
                  {record.event && (
                    <Tooltip title={record.event.type.description} arrow>
                      <span style={{ display: "inline-flex" }}>
                        <ReactSVG
                          src={utils.getMarkerAccordingVehicleEvent(record.event.type.id_event_type)}
                          beforeInjection={(svg) => {
                            svg.setAttribute("width", "16"); svg.setAttribute("height", "16");
                          }}
                        />
                      </span>
                    </Tooltip>
                  )}
                  {!record.vehicleState && !record.alert && !record.event && <RemoveIcon style={{ fontSize: 14 }} />}
                </span>
                <span style={{ display: "flex", gap: "2px", flexShrink: 0 }}>
                  <Tooltip title={t(TravelAnalysisMessages.coordinateViewGeneral)} arrow>
                    <IconButton size="small" onClick={() => setCommandDetail({ record, tab: "general" })}>
                      <InfoOutlinedIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t(TravelAnalysisMessages.coordinateViewContent)} arrow>
                    <IconButton size="small" onClick={() => setCommandDetail({ record, tab: "content" })}>
                      <DescriptionOutlinedIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t(TravelAnalysisMessages.coordinateViewResult)} arrow>
                    <IconButton size="small" onClick={() => setCommandDetail({ record, tab: "result" })}>
                      <AssessmentOutlinedIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t(CoordinateAnalysisMessages.showMarker)} arrow>
                    <IconButton size="small" onClick={() => setFocusTime({ time: new Date(record.occurrence_date).getTime(), nonce: Date.now() })}>
                      <RoomOutlinedIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </span>
              </div>
            ))}
          </Grid>
        </Grid>
      )}

      {/* Command Detail Dialog */}
      <CommandDetailDialog
        detail={commandDetail}
        onClose={() => setCommandDetail(null)}
      />
    </div>
  );
};

// region Command Detail Dialog

interface CommandDetailDialogProps {
  detail: { record: TravelAnalytics; tab: "general" | "content" | "result" } | null;
  onClose: () => void;
}

const TAB_TITLES: Record<string, TravelAnalysisMessages> = {
  general: TravelAnalysisMessages.coordinateViewGeneralData,
  content: TravelAnalysisMessages.coordinateViewCommandContent,
  result: TravelAnalysisMessages.coordinateViewProcessingResult
};

/** Renders a key-value table from an object. Handles nested objects gracefully. */
const renderObjectTable = (obj: Record<string, any> | null | undefined, t: (key: string) => string) => {
  if (!obj || Object.keys(obj).length === 0) {
    return <div style={{ color: "#999", padding: "8px 0" }}>{t(TravelAnalysisMessages.coordinateViewNoData)}</div>;
  }

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
      <thead>
        <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
          <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600 }}>{t(TravelAnalysisMessages.coordinateViewField)}</th>
          <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600 }}>{t(TravelAnalysisMessages.coordinateViewValue)}</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(obj).map(([key, value]) => (
          <tr key={key} style={{ borderBottom: "1px solid #f0f0f0" }}>
            <td style={{ padding: "4px 8px", fontWeight: 500, color: "#424242", verticalAlign: "top", whiteSpace: "nowrap" }}>
              {key}
            </td>
            <td style={{ padding: "4px 8px", color: "#616161", wordBreak: "break-all" }}>
              {formatCellValue(value)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/** Format a single cell value for display. */
const formatCellValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object" && value.content !== undefined) return String(value.content);
  if (Array.isArray(value) || typeof value === "object") {
    try {
      const json = JSON.stringify(value, null, 2);

      return (
        <pre style={{
          margin: 0, whiteSpace: "pre-wrap", fontSize: "11px",
          background: "#f5f5f5", padding: "4px 6px", borderRadius: "4px"
        }}>
          <code>{json}</code>
        </pre>
      );
    } catch { return "[object]"; }
  }

  return String(value);
};

/** Extracts "general" fields from a TravelAnalytics record (excludes content, processing_result, vehicleState, alert, event). */
const extractGeneralFields = (record: TravelAnalytics): Record<string, any> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { content, processing_result, vehicleState, alert, event, ...rest } = record as any;

  return rest;
};

const CommandDetailDialog: React.FC<CommandDetailDialogProps> = ({ detail, onClose }) => {
  const { t } = useTranslation();

  if (!detail) return null;

  const { record, tab } = detail;

  let data: Record<string, any> | null = null;

  switch (tab) {
  case "general":
    data = extractGeneralFields(record);
    break;
  case "content":
    data = record.content as any;
    break;
  case "result":
    data = (record as any).processing_result || null;
    break;
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth style={{ zIndex: 2100 }}>
      <DialogTitle>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{t(TAB_TITLES[tab])}</span>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers style={{ maxHeight: "60vh", overflow: "auto", padding: "8px 16px" }}>
        {renderObjectTable(data, t)}
      </DialogContent>
    </Dialog>
  );
};

// endregion

export default TravelCoordinateViewModal;
